/**
 * OnboardingWizard - Multi-step distributor setup wizard
 *
 * Steps:
 *   1. Company Profile
 *   2. Brand Setup
 *   3. Store Network
 *   4. Product Catalog
 *   5. Inventory Baseline
 *   6. Forecast Configuration
 *   7. Review & Activate
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../../services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OnboardingStepDef {
  step_number: number;
  step_name: string;
  description: string;
  is_completed: boolean;
  data: Record<string, any> | null;
}

interface OnboardingProgress {
  user_id: string;
  current_step: number;
  total_steps: number;
  steps: OnboardingStepDef[];
  is_completed: boolean;
  completed_at: string | null;
}

interface BrandEntry {
  name: string;
  category: string;
  supplier: string;
}

interface StoreEntry {
  name: string;
  type: string;
  city: string;
  country: string;
}

// ---------------------------------------------------------------------------
// Step labels for the MUI Stepper
// ---------------------------------------------------------------------------

const STEP_LABELS = [
  'Company Profile',
  'Brand Setup',
  'Store Network',
  'Product Catalog',
  'Inventory Baseline',
  'Forecast Configuration',
  'Review & Activate',
];

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const OnboardingWizard: React.FC = () => {
  const navigate = useNavigate();

  // Global state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0); // 0-indexed for MUI Stepper
  const [progress, setProgress] = useState<OnboardingProgress | null>(null);

  // Step 1 - Company Profile
  const [companyName, setCompanyName] = useState('');
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [businessType, setBusinessType] = useState('');

  // Step 2 - Brand Setup
  const [brands, setBrands] = useState<BrandEntry[]>([{ name: '', category: '', supplier: '' }]);

  // Step 3 - Store Network
  const [stores, setStores] = useState<StoreEntry[]>([{ name: '', type: 'standard', city: '', country: '' }]);

  // Step 4 - Product Catalog
  const [productUploadInstructions, setProductUploadInstructions] = useState('');

  // Step 5 - Inventory Baseline
  const [inventoryUploadInstructions, setInventoryUploadInstructions] = useState('');

  // Step 6 - Forecast Configuration
  const [forecastMethod, setForecastMethod] = useState('moving_average');
  const [coverageDays, setCoverageDays] = useState(30);
  const [safetyStockMethod, setSafetyStockMethod] = useState('fixed_percentage');

  // ---------------------------------------------------------------------------
  // Load or start onboarding
  // ---------------------------------------------------------------------------

  const initOnboarding = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let resp;
      try {
        resp = await api.getOnboardingProgress();
      } catch (err: any) {
        if (err?.response?.status === 404) {
          // Not started yet - start it
          resp = await api.startOnboarding();
          resp = { data: resp.data.progress };
        } else {
          throw err;
        }
      }
      const prog: OnboardingProgress = resp.data;
      setProgress(prog);

      if (prog.is_completed) {
        // Already completed, go to dashboard
        navigate('/dashboard', { replace: true });
        return;
      }

      // Set active step (API is 1-indexed, MUI is 0-indexed)
      setActiveStep(prog.current_step - 1);

      // Restore saved step data into form fields
      restoreStepData(prog);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load onboarding progress.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    initOnboarding();
  }, [initOnboarding]);

  // ---------------------------------------------------------------------------
  // Restore previously-saved step data
  // ---------------------------------------------------------------------------

  const restoreStepData = (prog: OnboardingProgress) => {
    for (const step of prog.steps) {
      if (!step.data) continue;
      const d = step.data;
      switch (step.step_number) {
        case 1:
          setCompanyName(d.company_name || '');
          setCountry(d.country || '');
          setRegion(d.region || '');
          setBusinessType(d.business_type || '');
          break;
        case 2:
          if (d.brands && d.brands.length > 0) {
            setBrands(d.brands);
          }
          break;
        case 3:
          if (d.stores && d.stores.length > 0) {
            setStores(d.stores);
          }
          break;
        case 4:
          setProductUploadInstructions(d.upload_instructions || '');
          break;
        case 5:
          setInventoryUploadInstructions(d.upload_instructions || '');
          break;
        case 6:
          setForecastMethod(d.forecast_method || 'moving_average');
          setCoverageDays(d.coverage_days || 30);
          setSafetyStockMethod(d.safety_stock_method || 'fixed_percentage');
          break;
        default:
          break;
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Gather current step data
  // ---------------------------------------------------------------------------

  const getStepData = (stepNumber: number): Record<string, any> => {
    switch (stepNumber) {
      case 1:
        return {
          company_name: companyName,
          country,
          region,
          business_type: businessType,
        };
      case 2:
        return { brands };
      case 3:
        return { stores };
      case 4:
        return { upload_instructions: productUploadInstructions || 'Will upload via Excel template' };
      case 5:
        return { upload_instructions: inventoryUploadInstructions || 'Will upload via Excel template' };
      case 6:
        return {
          forecast_method: forecastMethod,
          coverage_days: coverageDays,
          safety_stock_method: safetyStockMethod,
        };
      case 7:
        return { confirmed: true };
      default:
        return {};
    }
  };

  // ---------------------------------------------------------------------------
  // Navigation handlers
  // ---------------------------------------------------------------------------

  const handleNext = async () => {
    const stepNumber = activeStep + 1; // convert to 1-indexed
    setSaving(true);
    setError(null);

    try {
      const data = getStepData(stepNumber);
      const resp = await api.completeOnboardingStep(stepNumber, data);
      setProgress(resp.data);

      if (activeStep < STEP_LABELS.length - 1) {
        setActiveStep(activeStep + 1);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save step.');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(0, prev - 1));
    setError(null);
  };

  const handleComplete = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.completeOnboarding();
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to complete onboarding.');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Brand list helpers
  // ---------------------------------------------------------------------------

  const addBrand = () => setBrands([...brands, { name: '', category: '', supplier: '' }]);
  const removeBrand = (idx: number) => setBrands(brands.filter((_, i) => i !== idx));
  const updateBrand = (idx: number, field: keyof BrandEntry, value: string) => {
    const updated = [...brands];
    updated[idx] = { ...updated[idx], [field]: value };
    setBrands(updated);
  };

  // Store list helpers
  const addStore = () => setStores([...stores, { name: '', type: 'standard', city: '', country: '' }]);
  const removeStore = (idx: number) => setStores(stores.filter((_, i) => i !== idx));
  const updateStore = (idx: number, field: keyof StoreEntry, value: string) => {
    const updated = [...stores];
    updated[idx] = { ...updated[idx], [field]: value };
    setStores(updated);
  };

  // ---------------------------------------------------------------------------
  // Step content renderers
  // ---------------------------------------------------------------------------

  const renderStep1 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6">Company Profile</Typography>
      <Typography variant="body2" color="text.secondary">
        Tell us about your company. This information will be used to configure your account.
      </Typography>
      <TextField
        label="Company Name"
        value={companyName}
        onChange={(e) => setCompanyName(e.target.value)}
        required
        fullWidth
      />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Country</InputLabel>
            <Select value={country} label="Country" onChange={(e) => setCountry(e.target.value)}>
              <MenuItem value="Morocco">Morocco</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Region</InputLabel>
            <Select value={region} label="Region" onChange={(e) => setRegion(e.target.value)}>
              <MenuItem value="Morocco">Morocco</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <FormControl fullWidth required>
        <InputLabel>Business Type</InputLabel>
        <Select
          value={businessType}
          label="Business Type"
          onChange={(e) => setBusinessType(e.target.value)}
        >
          <MenuItem value="distributor">Distributor</MenuItem>
          <MenuItem value="retailer">Retailer</MenuItem>
          <MenuItem value="brand_owner">Brand Owner</MenuItem>
          <MenuItem value="wholesaler">Wholesaler</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );

  const renderStep2 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Typography variant="h6">Brand Setup</Typography>
          <Typography variant="body2" color="text.secondary">
            Add the brands you distribute. You can add more later.
          </Typography>
        </div>
        <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={addBrand}>
          Add Brand
        </Button>
      </Box>
      {brands.map((brand, idx) => (
        <Card key={idx} variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">Brand {idx + 1}</Typography>
              {brands.length > 1 && (
                <IconButton size="small" color="error" onClick={() => removeBrand(idx)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Brand Name"
                  value={brand.name}
                  onChange={(e) => updateBrand(idx, 'name', e.target.value)}
                  required
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={brand.category}
                    label="Category"
                    onChange={(e) => updateBrand(idx, 'category', e.target.value)}
                  >
                    <MenuItem value="Fragrance">Fragrance</MenuItem>
                    <MenuItem value="Make-up">Make-up</MenuItem>
                    <MenuItem value="Skincare">Skincare</MenuItem>
                    <MenuItem value="Haircare">Haircare</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Supplier"
                  value={brand.supplier}
                  onChange={(e) => updateBrand(idx, 'supplier', e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const renderStep3 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Typography variant="h6">Store Network</Typography>
          <Typography variant="body2" color="text.secondary">
            Define your retail store network. You can add more stores later.
          </Typography>
        </div>
        <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={addStore}>
          Add Store
        </Button>
      </Box>
      {stores.map((store, idx) => (
        <Card key={idx} variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">Store {idx + 1}</Typography>
              {stores.length > 1 && (
                <IconButton size="small" color="error" onClick={() => removeStore(idx)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Store Name"
                  value={store.name}
                  onChange={(e) => updateStore(idx, 'name', e.target.value)}
                  required
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Store Type</InputLabel>
                  <Select
                    value={store.type}
                    label="Store Type"
                    onChange={(e) => updateStore(idx, 'type', e.target.value)}
                  >
                    <MenuItem value="flagship">Flagship</MenuItem>
                    <MenuItem value="a_door">A-Door</MenuItem>
                    <MenuItem value="b_door">B-Door</MenuItem>
                    <MenuItem value="standard">Standard</MenuItem>
                    <MenuItem value="outlet">Outlet</MenuItem>
                    <MenuItem value="kiosk">Kiosk</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="City"
                  value={store.city}
                  onChange={(e) => updateStore(idx, 'city', e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={store.country}
                    label="Country"
                    onChange={(e) => updateStore(idx, 'country', e.target.value)}
                  >
                    <MenuItem value="Morocco">Morocco</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const renderStep4 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6">Product Catalog</Typography>
      <Typography variant="body2" color="text.secondary">
        You can import your product catalog later using an Excel template. For now, describe
        your catalog or leave the default instructions.
      </Typography>
      <Alert severity="info">
        After completing the onboarding wizard you will be able to download an Excel template
        from the Data Import section to bulk-upload your products.
      </Alert>
      <TextField
        label="Upload Instructions / Notes"
        value={productUploadInstructions}
        onChange={(e) => setProductUploadInstructions(e.target.value)}
        multiline
        rows={4}
        fullWidth
        placeholder="e.g. Will upload ~500 SKUs across Fragrance and Make-up categories via Excel template."
      />
    </Box>
  );

  const renderStep5 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6">Inventory Baseline</Typography>
      <Typography variant="body2" color="text.secondary">
        Set your initial stock levels. You can bulk-upload inventory data after onboarding.
      </Typography>
      <Alert severity="info">
        An Excel template for inventory upload will be available after you complete the wizard.
        You can enter approximate numbers or notes here.
      </Alert>
      <TextField
        label="Upload Instructions / Notes"
        value={inventoryUploadInstructions}
        onChange={(e) => setInventoryUploadInstructions(e.target.value)}
        multiline
        rows={4}
        fullWidth
        placeholder="e.g. We have stock data in our ERP for ~50 stores. Will export and upload."
      />
    </Box>
  );

  const renderStep6 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6">Forecast Configuration</Typography>
      <Typography variant="body2" color="text.secondary">
        Configure how IRIS generates demand forecasts for your products.
      </Typography>
      <FormControl fullWidth required>
        <InputLabel>Forecast Method</InputLabel>
        <Select
          value={forecastMethod}
          label="Forecast Method"
          onChange={(e) => setForecastMethod(e.target.value)}
        >
          <MenuItem value="moving_average">Moving Average</MenuItem>
          <MenuItem value="weighted_moving_average">Weighted Moving Average</MenuItem>
          <MenuItem value="exponential_smoothing">Exponential Smoothing</MenuItem>
          <MenuItem value="prophet">Prophet (Advanced)</MenuItem>
          <MenuItem value="arima">ARIMA</MenuItem>
          <MenuItem value="ml_ensemble">ML Ensemble</MenuItem>
        </Select>
      </FormControl>
      <TextField
        label="Coverage Days"
        type="number"
        value={coverageDays}
        onChange={(e) => setCoverageDays(parseInt(e.target.value) || 30)}
        required
        fullWidth
        helperText="Number of days of demand to cover with each replenishment order."
        inputProps={{ min: 7, max: 180 }}
      />
      <FormControl fullWidth required>
        <InputLabel>Safety Stock Method</InputLabel>
        <Select
          value={safetyStockMethod}
          label="Safety Stock Method"
          onChange={(e) => setSafetyStockMethod(e.target.value)}
        >
          <MenuItem value="fixed_percentage">Fixed Percentage (20% of avg demand)</MenuItem>
          <MenuItem value="statistical">Statistical (service level based)</MenuItem>
          <MenuItem value="demand_variability">Demand Variability</MenuItem>
          <MenuItem value="lead_time_based">Lead Time Based</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );

  const renderStep7 = () => {
    const completedSteps = progress?.steps.filter((s) => s.is_completed) || [];
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="h6">Review & Activate</Typography>
        <Typography variant="body2" color="text.secondary">
          Review your setup below. Once you are satisfied, click "Complete Setup" to activate your account.
        </Typography>

        {/* Company Profile Summary */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              1. Company Profile
            </Typography>
            {progress?.steps[0]?.data ? (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`Company: ${progress.steps[0].data.company_name}`} />
                <Chip label={`Country: ${progress.steps[0].data.country}`} />
                <Chip label={`Region: ${progress.steps[0].data.region}`} />
                <Chip label={`Type: ${progress.steps[0].data.business_type}`} />
              </Box>
            ) : (
              <Typography variant="body2" color="error">Not completed</Typography>
            )}
          </CardContent>
        </Card>

        {/* Brand Setup Summary */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              2. Brand Setup
            </Typography>
            {progress?.steps[1]?.data?.brands ? (
              <List dense disablePadding>
                {progress.steps[1].data.brands.map((b: any, i: number) => (
                  <ListItem key={i} disableGutters>
                    <ListItemText
                      primary={b.name}
                      secondary={`${b.category || 'No category'} | Supplier: ${b.supplier || 'N/A'}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="error">Not completed</Typography>
            )}
          </CardContent>
        </Card>

        {/* Store Network Summary */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              3. Store Network
            </Typography>
            {progress?.steps[2]?.data?.stores ? (
              <List dense disablePadding>
                {progress.steps[2].data.stores.map((s: any, i: number) => (
                  <ListItem key={i} disableGutters>
                    <ListItemText
                      primary={s.name}
                      secondary={`${s.type} | ${s.city}, ${s.country}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="error">Not completed</Typography>
            )}
          </CardContent>
        </Card>

        {/* Product Catalog Summary */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              4. Product Catalog
            </Typography>
            {progress?.steps[3]?.data ? (
              <Typography variant="body2">
                {progress.steps[3].data.upload_instructions || 'Will upload via Excel template'}
              </Typography>
            ) : (
              <Typography variant="body2" color="error">Not completed</Typography>
            )}
          </CardContent>
        </Card>

        {/* Inventory Baseline Summary */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              5. Inventory Baseline
            </Typography>
            {progress?.steps[4]?.data ? (
              <Typography variant="body2">
                {progress.steps[4].data.upload_instructions || 'Will upload via Excel template'}
              </Typography>
            ) : (
              <Typography variant="body2" color="error">Not completed</Typography>
            )}
          </CardContent>
        </Card>

        {/* Forecast Configuration Summary */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              6. Forecast Configuration
            </Typography>
            {progress?.steps[5]?.data ? (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`Method: ${progress.steps[5].data.forecast_method}`} />
                <Chip label={`Coverage: ${progress.steps[5].data.coverage_days} days`} />
                <Chip label={`Safety Stock: ${progress.steps[5].data.safety_stock_method}`} />
              </Box>
            ) : (
              <Typography variant="body2" color="error">Not completed</Typography>
            )}
          </CardContent>
        </Card>

        <Divider />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          <Typography variant="body1">
            {completedSteps.length} of {STEP_LABELS.length - 1} setup steps completed.
          </Typography>
        </Box>
      </Box>
    );
  };

  // ---------------------------------------------------------------------------
  // Step content dispatch
  // ---------------------------------------------------------------------------

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderStep1();
      case 1:
        return renderStep2();
      case 2:
        return renderStep3();
      case 3:
        return renderStep4();
      case 4:
        return renderStep5();
      case 5:
        return renderStep6();
      case 6:
        return renderStep7();
      default:
        return null;
    }
  };

  // ---------------------------------------------------------------------------
  // Check if current step is valid (basic client-side check)
  // ---------------------------------------------------------------------------

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 0:
        return !!companyName && !!country && !!region && !!businessType;
      case 1:
        return brands.length > 0 && brands.every((b) => !!b.name);
      case 2:
        return stores.length > 0 && stores.every((s) => !!s.name);
      case 3:
        return true; // upload instructions have a default
      case 4:
        return true; // upload instructions have a default
      case 5:
        return !!forecastMethod && coverageDays > 0 && !!safetyStockMethod;
      case 6:
        return true; // review step
      default:
        return false;
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isLastStep = activeStep === STEP_LABELS.length - 1;

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', py: 4, px: 2 }}>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Welcome to IRIS
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Let us set up your account. Complete each step below to get started with Intelligent Retail
        Inventory System.
      </Typography>

      {/* Stepper */}
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {STEP_LABELS.map((label, index) => {
          const stepCompleted = progress?.steps[index]?.is_completed || false;
          return (
            <Step key={label} completed={stepCompleted}>
              <StepLabel
                StepIconProps={{
                  sx: stepCompleted ? { color: 'success.main' } : undefined,
                }}
              >
                {label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>

      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step content */}
      <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
        {renderStepContent(activeStep)}
      </Paper>

      {/* Navigation buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          disabled={activeStep === 0 || saving}
          onClick={handleBack}
          variant="outlined"
        >
          Back
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {isLastStep ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleComplete}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={18} /> : <CheckCircleIcon />}
            >
              {saving ? 'Completing...' : 'Complete Setup'}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={!isStepValid(activeStep) || saving}
              startIcon={saving ? <CircularProgress size={18} /> : undefined}
            >
              {saving ? 'Saving...' : 'Next'}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingWizard;
